import React from 'react';
import PropTypes from 'prop-types';
import { Autocomplete as MuiAutocomplete } from '@mui/material';
import './Field.css';

/**
 * Field — Talk to us form pattern.
 * Fragment Mono uppercase label + control slot + help/error.
 */
export function Field({
  label,
  htmlFor,
  required = false,
  help,
  error,
  density = 'comfortable',
  children,
  className = '',
}) {
  return (
    <div
      className={[
        'onyx-field',
        density === 'compact' ? 'onyx-field--compact' : '',
        className,
      ].filter(Boolean).join(' ')}
      data-testid="onyx-field"
      data-density={density}
    >
      {label ? (
        <label className="onyx-field__label" htmlFor={htmlFor}>
          {label}
          {required ? <span className="onyx-field__required" aria-hidden="true">*</span> : null}
        </label>
      ) : null}
      <div className="onyx-field__control">{children}</div>
      {error ? (
        <p className="onyx-field__error" role="alert">{error}</p>
      ) : help ? (
        <p className="onyx-field__help">{help}</p>
      ) : null}
    </div>
  );
}

Field.propTypes = {
  label: PropTypes.node,
  htmlFor: PropTypes.string,
  required: PropTypes.bool,
  help: PropTypes.node,
  error: PropTypes.node,
  density: PropTypes.oneOf(['comfortable', 'compact']),
  children: PropTypes.node,
  className: PropTypes.string,
};

export function TextInput({
  className = '',
  type = 'text',
  density = 'comfortable',
  ...props
}) {
  return (
    <input
      type={type}
      className={[
        'onyx-input',
        density === 'compact' ? 'onyx-input--compact' : '',
        className,
      ].filter(Boolean).join(' ')}
      data-density={density}
      {...props}
    />
  );
}

TextInput.propTypes = {
  className: PropTypes.string,
  type: PropTypes.string,
  density: PropTypes.oneOf(['comfortable', 'compact']),
};

export const TextArea = React.forwardRef(function TextArea(
  { className = '', rows = 6, ...props },
  ref,
) {
  return (
    <textarea
      ref={ref}
      className={['onyx-input', 'onyx-input--textarea', className].filter(Boolean).join(' ')}
      rows={rows}
      {...props}
    />
  );
});

TextArea.propTypes = {
  className: PropTypes.string,
  rows: PropTypes.number,
};

export function Select({ className = '', density = 'comfortable', children, ...props }) {
  return (
    <select
      className={[
        'onyx-input',
        'onyx-input--select',
        density === 'compact' ? 'onyx-input--compact' : '',
        className,
      ].filter(Boolean).join(' ')}
      data-density={density}
      {...props}
    >
      {children}
    </select>
  );
}

Select.propTypes = {
  className: PropTypes.string,
  density: PropTypes.oneOf(['comfortable', 'compact']),
  children: PropTypes.node,
};

/**
 * ChoiceGroup — Interest-style tiles (Talk to us SoT).
 * type="radio" | "checkbox".
 * layout="stack" (default) — one full-width tile per option (survey / forms).
 * layout="pair" — optional 2-up row for short binary marketing prompts.
 */
export function ChoiceGroup({
  name,
  type = 'radio',
  options = [],
  value,
  values,
  onChange,
  layout = 'stack',
  density = 'comfortable',
  className = '',
}) {
  const isMulti = type === 'checkbox';
  const selected = isMulti ? (values || []) : value;
  const usePair = layout === 'pair' && !isMulti && options.length === 2;

  const handleChange = (optionValue) => {
    if (!onChange) return;
    if (isMulti) {
      const next = selected.includes(optionValue)
        ? selected.filter((v) => v !== optionValue)
        : [...selected, optionValue];
      onChange(next);
    } else {
      onChange(optionValue);
    }
  };

  return (
    <div
      className={[
        'onyx-choice-group',
        usePair ? 'onyx-choice-group--pair' : '',
        density === 'compact' ? 'onyx-choice-group--compact' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      role={isMulti ? 'group' : 'radiogroup'}
      data-testid="onyx-choice-group"
      data-layout={usePair ? 'pair' : 'stack'}
      data-density={density}
    >
      {options.map((opt) => {
        const optionValue = typeof opt === 'string' ? opt : opt.value;
        const optionLabel = typeof opt === 'string' ? opt : opt.label;
        const checked = isMulti
          ? selected.includes(optionValue)
          : selected === optionValue;
        return (
          <label
            key={String(optionValue)}
            className={[
              'onyx-choice',
              checked ? 'onyx-choice--selected' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <input
              type={type}
              name={name}
              value={optionValue}
              checked={checked}
              onChange={() => handleChange(optionValue)}
              className={
                type === 'radio' ? 'onyx-choice__radio' : 'onyx-choice__checkbox'
              }
            />
            <span className="onyx-choice__label">{optionLabel}</span>
          </label>
        );
      })}
    </div>
  );
}

ChoiceGroup.propTypes = {
  name: PropTypes.string,
  type: PropTypes.oneOf(['radio', 'checkbox']),
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ value: PropTypes.any, label: PropTypes.node }),
    ]),
  ),
  value: PropTypes.any,
  values: PropTypes.array,
  onChange: PropTypes.func,
  layout: PropTypes.oneOf(['stack', 'pair']),
  density: PropTypes.oneOf(['comfortable', 'compact']),
  className: PropTypes.string,
};

export function FormActions({ children, className = '', align = 'end' }) {
  return (
    <div
      className={[
        'onyx-form-actions',
        align !== 'end' ? `onyx-form-actions--${align}` : '',
        className,
      ].filter(Boolean).join(' ')}
      data-testid="onyx-form-actions"
      data-align={align}
    >
      {children}
    </div>
  );
}

FormActions.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  align: PropTypes.oneOf(['start', 'center', 'end']),
};

export function Autocomplete({ className = '', renderInput, ...props }) {
  return (
    <MuiAutocomplete
      {...props}
      className={['onyx-autocomplete', className].filter(Boolean).join(' ')}
      data-testid="onyx-autocomplete"
      renderInput={
        renderInput
        || ((params) => (
          <div ref={params.InputProps.ref}>
            <input className="onyx-input" {...params.inputProps} />
            {params.InputProps.endAdornment}
          </div>
        ))
      }
    />
  );
}

Autocomplete.propTypes = {
  className: PropTypes.string,
  renderInput: PropTypes.func,
};
